/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICache } from '@msdyn365-commerce/cache-internal';
import { IImageSettings, IPartnerThemeSettings } from '@msdyn365-commerce/core-internal';
import { cloneDeep as _cloneDeep, get as _get, set as _set } from 'lodash';
import {
    MODULE_ACTION_KEY,
    MODULE_CACHE_KEY,
    MODULE_CONFIG_ERRORS_KEY,
    MODULE_CONFIG_KEY,
    MODULE_DATA_KEY,
    MODULE_DEFERRED_KEY
} from '../consts';

export interface IImageData {
    $type: string;
    altText: string;
    source: string;
    imageQuality: string;
    mode?: number;
    backgroundColor?: string;
    title: string;
    focalRegion?: IFocalRegion;
    cropRegions?: ICropRegion[];
    format?: string;
}

/**
 * cms datatype for focal region
 */
export interface IFocalRegion {
    x1?: number;
    y1?: number;
    x2?: number;
    y2?: number;
}

/**
 * cms datatype for crop region
 */

export interface ICropRegion {
    top?: number;
    left?: number;
    width?: number;
    height?: number;
    targetWidth?: number;
    targetHeight?: number;
    name: string;
}

export interface IBinaryReference {
    format?: string;
    clientHref?: string;
    alias?: string;
    contentType?: string;
    extension?: string;
    sourceHref?: string;
}

export interface IVideoData {
    fileName: string;
    title: string;
    playTime: number;
    thumbnail: IImageData;
    interactiveTriggersEnabled?: boolean;
    binaryReferences: IBinaryReference[];
}

interface IImageResizerQueryParams {
    /* Image width */
    w: string;

    /* Image height */
    h: string;

    /* Image quality */
    q: string;

    /* Image mode, 6 represents focal point mode and 8 represents crop region mode.
    6 is the default value if neither focal or crop region information is present */
    m: string;

    /* Used to toggle focal mode. If o=t use focal crop. If o=f disable focal point crop */
    o?: string;

    /* X cooridnate for focal point or crop region start */
    x?: string;

    /* Y coordinate for focal point or crop region start */
    y?: string;

    /* Image size, used with crop regions */
    s?: string;

    /* Image depth, used with crop regions */
    d?: string;

    /* Image format, e.g. jpg, png */
    f?: string;
}

/**
 * Resolves any image/video types in the module config as given by authoring in a fragment or a module
 * into what is required to render these images/videos on SDK side
 */
export const resolveTypesInConfig = (
    config: any,
    moduleId: string,
    moduleType: string,
    moduleLayout: string | undefined,
    themeSettings: IPartnerThemeSettings
) => {
    resolveTypesInConfigHelper(config, moduleId, moduleType, moduleLayout, [], themeSettings);
};

const resolveTypesInConfigHelper = (
    obj: any,
    moduleId: string,
    moduleType: string,
    moduleLayout: string | undefined,
    pathToField: string[],
    themeSettings: IPartnerThemeSettings
) => {
    if (!obj) {
        return;
    } else if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
            resolveTypesInConfigHelper(obj[i], moduleId, moduleType, moduleLayout, pathToField.concat([`${i}`]), themeSettings);
        }
    } else if (typeof obj === 'object' && obj) {
        Object.keys(obj).forEach((value: string) => {
            const updatedPathToField = pathToField.concat([`${value}`]);
            if (obj[value].type === 'imageFileData') {
                const imageData = obj[value];

                // Construct the viewport image settings information using the current theme and module
                const viewportInfo = constructViewportInformationForImage(
                    moduleType,
                    moduleLayout,
                    updatedPathToField,
                    imageData.binaryMetadata?.quality || 80,
                    themeSettings,
                    imageData.binaryMetadata?.focalRegion,
                    imageData.binaryMetadata?.cropRegions,
                    imageData.binaryMetadata?.extension
                ).viewports;
                obj[value] = {
                    src: imageData.publicUrl,
                    $type: 'image',
                    altText: imageData.variantInfo?.variantProperties?.altText,
                    title: imageData.variantInfo?.variantProperties?.title,
                    imageSettings: {
                        quality: imageData.binaryMetadata?.quality,
                        viewports: viewportInfo
                    }
                };
            } else if (obj[value].type === 'videoFileData') {
                const authoringVideoData = obj[value];
                const videoData: IVideoData = {
                    binaryReferences: authoringVideoData.binaryMetadata?.binaryReferences,
                    title: authoringVideoData.binaryMetadata?.title,
                    playTime: authoringVideoData.binaryMetadata?.playtime,
                    fileName: authoringVideoData.sourceUrl,
                    interactiveTriggersEnabled: authoringVideoData.variantProperties?.interactiveTriggersEnabled,
                    thumbnail: {
                        $type: 'image',
                        source: authoringVideoData.thumbnailUrl,
                        imageQuality: '80',
                        altText: authoringVideoData.binaryMetadata?.thumbnail?.title,
                        title: authoringVideoData.binaryMetadata?.thumbnail?.altText
                    }
                };
                obj[value] = constructVideoInformation(videoData, moduleId);
            } else {
                resolveTypesInConfigHelper(obj[value], moduleId, moduleType, moduleLayout, updatedPathToField, themeSettings);
            }
        });
    }
};

/**
 * Constructs the viewport information for an image using the current module and theme settings
 * Also constructs the query string for the image resizer
 *
 * @param moduleId The id of the module
 * @param moduleConfig The config object for the module
 * @param pathToField The path to the image the field
 * @param quality The image quality selected
 * @param themeSettings The current theme settings
 * @param focalRegion The image focal region settings
 * @param cropRegions The image crop region per layout/viewport settings
 * @param format The image format
 */
export const constructViewportInformationForImage = (
    moduleName: string,
    moduleLayout: string | undefined,
    pathToField: string[],
    quality: string | undefined,
    themeSettings: IPartnerThemeSettings,
    focalRegion: IFocalRegion = {},
    cropRegions: ICropRegion[] = [],
    format?: string
): any => {
    const imageSettings = getImageViewportSettingsForModuleLayout(themeSettings, moduleLayout, moduleName, pathToField);
    // Construct the query string to use for the image resizer given the width, height and quality selected
    Object.keys(imageSettings.viewports).forEach((viewport: string) => {
        const currentViewport = imageSettings.viewports[viewport];
        const width = getWidth(currentViewport);
        const height = getHeight(currentViewport);
        currentViewport.q = constructImageQueryString(
            width,
            height,
            quality,
            focalRegion,
            cropRegions,
            moduleName,
            moduleLayout,
            pathToField,
            viewport,
            format
        );
    });

    // resolve .h and .w propety from .height and .width of viewports.
    Object.keys(imageSettings.viewports).forEach(key => {
        if (imageSettings.viewports[key].height) {
            imageSettings.viewports[key].h = imageSettings.viewports[key].h || imageSettings.viewports[key].height;
            delete imageSettings.viewports[key].height;
        }
        if (imageSettings.viewports[key].width) {
            imageSettings.viewports[key].w = imageSettings.viewports[key].w || imageSettings.viewports[key].width;
            delete imageSettings.viewports[key].width;
        }
    });
    return imageSettings;
};

const getWidth = (currentViewport: any) => {
    if (currentViewport.w) {
        return currentViewport.w;
    } else if (currentViewport.width) {
        return currentViewport.width;
    }
    return undefined;
};

const getHeight = (currentViewport: any) => {
    if (currentViewport.h) {
        return currentViewport.h;
    } else if (currentViewport.height) {
        return currentViewport.height;
    }
    return undefined;
};

/**
 * Constructs the image resizer query param string
 * taking into account width, height, quality, focal point and crop regions
 */
const constructImageQueryString = (
    width: string,
    height: string,
    quality: string | undefined,
    focalRegion: IFocalRegion,
    cropRegions: ICropRegion[],
    moduleName: string,
    moduleLayout: string | undefined,
    pathToField: string[],
    viewport: string,
    format?: string
): string => {
    // Set the base properties
    const resizerProperties: IImageResizerQueryParams = {
        w: width,
        h: height,
        q: quality || '',
        m: '6'
    };

    // If focal region properties exist set the focal point information
    if (focalRegion && focalRegion.x1 && focalRegion.y1) {
        resizerProperties.o = 't';
        resizerProperties.x = `${focalRegion.x1}`;
        resizerProperties.y = `${focalRegion.y1}`;
    }

    const pathToFieldString = pathToField.join('|');
    const cropRegion = cropRegions.find(
        (value: ICropRegion) => value.name === `${moduleName}|${moduleLayout}|${pathToFieldString}|${viewport}|${width}x${height}`
    );
    // If crop region exists for the particular viewport and layout
    // If both focal and crop region information exist, crop region takes precedence
    if (cropRegion) {
        resizerProperties.o = undefined;
        resizerProperties.m = '8';
        resizerProperties.x = `${cropRegion.left}`;
        resizerProperties.y = `${cropRegion.top}`;
        resizerProperties.s = `${cropRegion.width}`;
        resizerProperties.d = `${cropRegion.height}`;
    }

    // Add format information if available
    if (format) {
        resizerProperties.f = format.slice(1);
    }

    let queryString: string = '';
    Object.keys(resizerProperties).forEach((key: string) => {
        if (resizerProperties[key] && resizerProperties[key] !== '') {
            queryString += `${key}=${resizerProperties[key]}&`;
        }
    });
    // Remove trailing ampersand if length greater than 0
    return queryString.length > 0 ? queryString.slice(0, -1) : queryString;
};

// Moves an element in the given array to the newIndex,
// pushing all elements after the new index to the right
export const moveArrayItem = (array: any[], newIndex: number, newElement: any): void => {
    array.splice(newIndex, 0, newElement);
};

/**
 * Deletes all the cache entries for a specific module id
 *
 * @param moduleId The module id of the module whose cache entries are to be deleted
 * @param cache The cache object
 */
export const deleteCacheEntriesForModuleId = (moduleId: string, cache: ICache): void => {
    // Delete module from all cache types
    cache.del({ typeName: MODULE_CONFIG_ERRORS_KEY, key: moduleId });
    cache.del({ typeName: MODULE_CONFIG_KEY, key: moduleId });
    cache.del({ typeName: MODULE_CACHE_KEY, key: moduleId });
    cache.del({ typeName: MODULE_ACTION_KEY, key: moduleId });
    cache.del({ typeName: MODULE_DATA_KEY, key: moduleId });
    cache.del({ typeName: MODULE_DEFERRED_KEY, key: moduleId });
};

/**
 * Constructs a video object used to render a video using data given by authoring
 *
 * @param videoData The video data given by authoring
 * @param moduleId The module id of the module for which this information is being constructed
 */
export const constructVideoInformation = (videoData: IVideoData, moduleId: string, id?: string): any => {
    // Used to generate unique keys so that even reused module ID will still trigger re-render of player component
    const uniqueTime = new Date().getTime();
    return {
        $type: 'video',
        id: `${moduleId}:${uniqueTime}`,
        _links: {
            binaryReferences: videoData.binaryReferences
        },
        playTime: videoData.playTime,
        title: videoData.title,
        fileName: videoData.fileName,
        thumbnail: {
            $type: 'image',
            src: videoData.thumbnail.source,
            title: videoData.thumbnail.title,
            quality: videoData.thumbnail.imageQuality
        },
        interactiveTriggersEnabled: videoData.interactiveTriggersEnabled || false
    };
};

/**
 * Gets the property value of the specified property key
 * from a data-i attribute string
 *
 * @param dataIAttribute data-i attribute string
 * @param key property key
 */
export const getPropertyFromDataIAttribute = (dataIAttribute: string, key: string) => {
    dataIAttribute = dataIAttribute.slice(1, -1);
    const properties = dataIAttribute?.split(',');
    const obj = {};
    properties?.forEach((property: string) => {
        const keyValuePairs = property.split(':');
        obj[keyValuePairs[0]] = keyValuePairs[1];
    });
    return obj[key];
};

/**
 * Returns the image viewport settings for a given image in a module for a given layouy
 *
 * @param themeSettings The theme settings currently being used
 * @param moduleLayout The current module layout
 * @param moduleName The name of the module
 * @param pathToField The path to the image field inside the module config
 */
export const getImageViewportSettingsForModuleLayout = (
    themeSettings: IPartnerThemeSettings,
    moduleLayout: string | undefined,
    moduleName: string,
    pathToField: string[]
) => {
    const imageSettings = {
        viewports: {
            xs: {
                width: 800,
                height: 600
            },
            sm: {
                width: 1200,
                height: 900
            },
            md: {
                width: 1600,
                height: 900
            },
            lg: {
                width: 1600,
                height: 700
            },
            xl: {
                width: 1600,
                height: 700
            }
        }
    };

    // Drill down into the theme settings object for the current module, current module layout and image property name
    if (themeSettings && themeSettings.modules && moduleLayout) {
        const themeSettingsForModule = themeSettings.modules[moduleName];
        if (themeSettingsForModule) {
            const themeSettingsForLayout = themeSettingsForModule.properties[moduleLayout];
            if (themeSettingsForLayout) {
                const imageViewportProperties = _get(themeSettingsForLayout.properties, pathToField, { properties: imageSettings })
                    .properties;
                // Clone the image viewport properties from theme settings
                // so that the proxy traps do not affect the global themesettings
                imageSettings.viewports = _cloneDeep(imageViewportProperties);
            }
        }
    }
    return imageSettings;
};

/**
 * Takes in a module config and modifies the image viewport properties for all defined images
 * to reflect the new layout
 *
 * @param moduleConfig The module config object
 * @param moduleType The module type
 * @param layout The new layout
 * @param themeSettings The theme settings for the currently applied theme
 * @param imagePaths 2D array of all the image paths present in the module
 */
export const changeViewportInfoForImagesForNewLayout = (
    moduleConfig: object | null,
    moduleType: string,
    layout: string,
    themeSettings: IPartnerThemeSettings,
    imagePaths: string[][]
): void => {
    if (!moduleConfig) {
        return;
    }
    imagePaths.forEach(pathArray => {
        // Check if the image property exists and has any info
        const imageData = _get(moduleConfig, pathArray);
        if (imageData && Object.keys(imageData).length > 0) {
            const imageSettingPathArray = pathArray.concat('imageSettings');
            const imageSettings: IImageSettings = _get(moduleConfig, imageSettingPathArray);
            let newImageSettings: any;
            // If image settings viewports is already defined we need to change viewport information without affecting any of the other set properties (crop region, focal point, etc.)
            if (imageSettings && imageSettings.viewports && Object.keys(imageSettings.viewports).length > 0) {
                newImageSettings = _cloneDeep(imageSettings);
                let oldQueryString: string;
                // Grab an old query string from any one of the old viewports
                const oldViewports = Object.keys(imageSettings.viewports);
                for (let i = 0; i < oldViewports.length; i++) {
                    if (imageSettings.viewports[oldViewports[i]]?.q) {
                        oldQueryString = imageSettings.viewports[oldViewports[i]]?.q;
                        break;
                    }
                }
                newImageSettings.viewports = getImageViewportSettingsForModuleLayout(
                    themeSettings,
                    layout,
                    moduleType,
                    pathArray
                ).viewports;
                // Replace viewport information with new viewport information
                Object.keys(newImageSettings.viewports).forEach((viewport: string) => {
                    const currentViewport = newImageSettings.viewports[viewport];
                    const width = getWidth(currentViewport);
                    const height = getHeight(currentViewport);
                    newImageSettings.viewports[viewport].w = width;
                    newImageSettings.viewports[viewport].h = height;
                    delete newImageSettings.viewports[viewport].height;
                    delete newImageSettings.viewports[viewport].height;
                    // Transform the query string to use the new width and height
                    // Replace the width information in the query string
                    let newQueryString = oldQueryString.replace(/w=\d*/i, `w=${width}`);
                    // Replace the height information in the query string
                    newQueryString = newQueryString.replace(/h=\d*/i, `h=${height}`);
                    newImageSettings.viewports[viewport].q = newQueryString;
                });
            } else {
                // If image setting information is not available construct brand new image settings
                newImageSettings = constructViewportInformationForImage(moduleType, layout, pathArray, undefined, themeSettings);
            }
            _set(moduleConfig, imageSettingPathArray, newImageSettings);
        }
    });
};
